import { StyleSheet } from 'react-native';
import { AdminColors } from '../../../../../Admin/constants/adminColors';
import FontFamily from '../../../../../../constants/fonts';

// HTML reference:
//   .people-cmx-scroll   -> tableContainer (outer scrollable border box)
//   .people-cmx-table    -> split-panel layout
//   Left panel = sticky Metric col + optional 12-Wk Avg + Weeks Met
//   Right panel = horizontally-scrollable week columns
//
// Colors (HTML CSS vars -> mapped values):
//   --nav-blue-50        -> AdminColors.blue50   (#E8F0FB)   thead bg
//   --nav-blue-75        -> AdminColors.blue75   (#DAEEFA)   met cell bg
//   --nav-blue-100       -> AdminColors.borderTable (#D7E5F8) border
//   --nav-blue-900       -> AdminColors.blue900  (#01346F)   met text
//   --nav-red-50         -> '#FBE9E4'            miss cell bg
//   --nav-red-700        -> AdminColors.red700   (#AB2B09)   miss text
//   --nav-blue-500       -> AdminColors.blue700  (#005CBC)   current border
//   --nav-bg-muted       -> AdminColors.nearWhite (#F8F8F8)  pending bg

const THEAD_BG   = AdminColors.blue50;      // #E8F0FB
const BORDER_COL = AdminColors.borderTable; // #D7E5F8
const MET_BG     = AdminColors.blue75;      // #DAEEFA
const MET_TEXT   = AdminColors.blue900;     // #01346F
const MISS_BG    = '#FBE9E4';               // nav-red-50
const MISS_TEXT  = AdminColors.red700;      // #AB2B09
const PEND_BG    = AdminColors.nearWhite;   // #F8F8F8
const CURR_BORDER= AdminColors.blue700;     // #005CBC (nav-blue-500 closest)

const ROW_H   = 58;  // enough for value + small sub-label
const METRIC_W = 140; // px  (first frozen col)
const SUMMARY_W = 82; // px  (12-Wk Avg / Weeks Met)
const WEEK_W  = 92;  // px  (each week col, matches HTML min-width: 92px)

const styles = StyleSheet.create({
  // ── Week / Period toggle ───────────────────────────────────────────────
  toggleRow: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    overflow: 'hidden',
  },
  togglePill: { paddingHorizontal: 12, paddingVertical: 6, backgroundColor: AdminColors.bgSurface },
  togglePillActive: { backgroundColor: AdminColors.blue25 },
  togglePillText: { fontFamily: FontFamily.medium, fontSize: 13, color: AdminColors.textSecondary },
  togglePillTextActive: { color: AdminColors.blue800, fontFamily: FontFamily.bold },


  // ── Loading / empty states ────────────────────────────────────────────
  stateContainer: { padding: 32, alignItems: 'center', justifyContent: 'center', gap: 8 },
  stateText: { fontFamily: FontFamily.regular, fontSize: 13, color: AdminColors.textSecondary, textAlign: 'center' },

  // ── Split-panel container ─────────────────────────────────────────────
  // Wraps [leftPanel | rightScroll] horizontally.
  // The outer card has overflow:hidden so the rounded corners clip the table.
  tableContainer: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: BORDER_COL,
    borderRadius: 10,
    overflow: 'hidden',
  },

  // ── Left frozen panel ─────────────────────────────────────────────────
  // Contains Metric col + optional 12-Wk Avg + Weeks Met
  leftPanel: {
    flexDirection: 'column',
    borderRightWidth: 1,
    borderRightColor: BORDER_COL,
    backgroundColor: AdminColors.bgSurface,
    zIndex: 1, // sits above right scroll shadow
  },

  // ── Right scrollable panel ────────────────────────────────────────────
  // flexGrow: 1 makes the content container fill the full scroll-view width
  // when the columns are narrower than the available space (observation mode).
  rightScroll: { flex: 1 },
  rightScrollContent: { flexGrow: 1, flexDirection: 'column' },
  // Used instead of rightScroll/rightScrollContent when there are no summary columns \u2014
  // a plain flex-filling view (no horizontal scroll) so week columns stretch evenly.
  rightFlexPanel: { flex: 1, flexDirection: 'column' },

  // ── Shared header row ─────────────────────────────────────────────────
  theadRow: {
    flexDirection: 'row',
    backgroundColor: THEAD_BG,
    borderBottomWidth: 2,
    borderBottomColor: BORDER_COL,
    minHeight: 42,
  },

  // Header cells
  thMetricCell: {
    width: METRIC_W,
    paddingHorizontal: 10,
    paddingVertical: 8,
    justifyContent: 'center',
  },
  thSummaryCell: {
    width: SUMMARY_W,
    paddingHorizontal: 6,
    paddingVertical: 8,
    justifyContent: 'center',
    alignItems: 'center',
    borderLeftWidth: 1,
    borderLeftColor: BORDER_COL,
  },
  thWeekCell: {
    width: WEEK_W,
    paddingHorizontal: 6,
    paddingVertical: 8,
    justifyContent: 'center',
    alignItems: 'center',
    borderLeftWidth: 1,
    borderLeftColor: BORDER_COL,
  },
  // Used instead of thWeekCell when there are no 12-Wk Avg / Weeks Met columns, so the
  // week columns stretch evenly to fill the full table width instead of leaving a gap.
  thWeekCellFlex: {
    flex: 1,
    width: undefined,
    minWidth: 0,
  },
  thText: {
    fontFamily: FontFamily.bold,
    fontSize: 11,
    color: AdminColors.blue900,
    textTransform: 'uppercase',
    textAlign: 'center',
    letterSpacing: 0.3,
  },

  // ── Shared data row ───────────────────────────────────────────────────
  dataRow: {
    flexDirection: 'row',
    minHeight: ROW_H,
    borderBottomWidth: 1,
    borderBottomColor: BORDER_COL,
    backgroundColor: AdminColors.bgSurface,
  },

  // ── Left panel data cells ─────────────────────────────────────────────
  tdMetricCell: {
    width: METRIC_W,
    paddingHorizontal: 10,
    paddingVertical: 10,
    justifyContent: 'center',
  },
  tdSummaryCell: {
    width: SUMMARY_W,
    paddingHorizontal: 6,
    paddingVertical: 10,
    justifyContent: 'center',
    alignItems: 'center',
    borderLeftWidth: 1,
    borderLeftColor: BORDER_COL,
    backgroundColor: AdminColors.bgSurface,
  },

  // Metric name + trend button
  trendButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metricNameText: {
    fontFamily: FontFamily.bold,
    fontSize: 13,
    color: AdminColors.textLink,
  },
  metricUnitText: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
    color: AdminColors.textSecondary,
    marginTop: 3,
  },

  // Summary cell values (12-Wk Avg / Weeks Met)
  summaryValue: {
    fontFamily: FontFamily.bold,
    fontSize: 13,
    color: AdminColors.textPrimary,
    textAlign: 'center',
  },
  summaryPct: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
    color: AdminColors.textSecondary,
    textAlign: 'center',
    marginTop: 2,
  },

  // ── Week cells ────────────────────────────────────────────────────────
  weekCell: {
    width: WEEK_W,
    minHeight: ROW_H,
    paddingHorizontal: 6,
    paddingVertical: 10,
    justifyContent: 'center',
    alignItems: 'center',
    borderLeftWidth: 1,
    borderLeftColor: BORDER_COL,
    backgroundColor: AdminColors.bgSurface,
  },
  // Pairs with thWeekCellFlex — see note above.
  weekCellFlex: {
    flex: 1,
    width: undefined,
    minWidth: 0,
  },
  weekCellMet:     { backgroundColor: MET_BG },
  weekCellMiss:    { backgroundColor: MISS_BG },
  weekCellPending: { backgroundColor: PEND_BG },
  weekCellCurrent: { shadowColor: CURR_BORDER, shadowOffset: { width: 0, height: 0 }, shadowOpacity: 1, shadowRadius: 0, borderWidth: 2, borderColor: CURR_BORDER },

  weekCellValue: {
    fontFamily: FontFamily.bold,
    fontSize: 13,
    textAlign: 'center',
  },
  weekCellValueMet:     { color: MET_TEXT },
  weekCellValueMiss:    { color: MISS_TEXT },
  weekCellValuePending: { color: AdminColors.textMuted },
  weekCellValueNeutral: { color: AdminColors.textPrimary },

  weekCellSub: {
    fontFamily: FontFamily.regular,
    fontSize: 10,
    color: AdminColors.textSecondary,
    textAlign: 'center',
    marginTop: 3,
  },
  weekCellSubColored: { color: 'inherit' } as any,

  // ── Legend ────────────────────────────────────────────────────────────
  legendRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 16,
    paddingTop: 16,
    marginTop: 4,
    borderTopWidth: 1,
    borderTopColor: AdminColors.borderDefault,
  },
  legendItem: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  legendSquare: { width: 11, height: 11, borderRadius: 2, borderWidth: 1, borderColor: AdminColors.borderDefault },
  legendSquareMet:  { backgroundColor: MET_BG,  borderColor: BORDER_COL },
  legendSquareMiss: { backgroundColor: MISS_BG, borderColor: '#F5C0B0' },
  legendText: { fontFamily: FontFamily.regular, fontSize: 12, color: AdminColors.textSecondary },
  legendDisclaimer: { fontFamily: FontFamily.regular, fontSize: 11, color: AdminColors.textMuted, flexShrink: 1 },
});

export default styles;
